import React, { useState, useEffect, useMemo } from 'react';
import { Typography } from 'antd';
import { TextShimmer } from './TextShimmer';
import { useMissionState } from '../hooks/useMissionState';
import { MissionModal } from './MissionModal';
import { GlowEffect } from './GlowEffect';
import { MissionLoader } from './MissionLoader';
import type { MissionWithRelations, ParcoursWithRelations } from '../types/database';

const { Paragraph } = Typography;

// Pré-charger tous les composants de mission avec import.meta.glob
const missionComponents = import.meta.glob('../missions/**/*.tsx');

interface MissionDisplayProps {
  hidden:any;
  setHidden:any;
  mission: MissionWithRelations;
  parcours: ParcoursWithRelations;
  currentMissionIndex: number;
}

export const MissionDisplay: React.FC<MissionDisplayProps> = ({
  hidden,
  setHidden,
  mission,
  parcours,
  currentMissionIndex
}) => {
  const { missionState, resetMission } = useMissionState();
  const [showModal, setShowModal] = useState(false);

  // Fabrique la fonction d'import dynamique si mission démarrée et code fourni
  const importer = useMemo(() => {
    if (missionState.isStarted && mission.codeMission) {
      // Construire le bon chemin: ../missions/parcours-1/mission-1.tsx
      const componentKey = `../missions/${mission.codeMission}.tsx`;
      if (missionComponents[componentKey]) {
        return missionComponents[componentKey] as () => Promise<{ default: React.ComponentType<any> }>;
      }
    }
    return null;
  }, [missionState.isStarted, mission.codeMission]);

  // Afficher la modale quand la mission est terminée
  useEffect(() => {
    if (missionState.isCompleted) {
      setShowModal(true);
    }
  }, [missionState.isCompleted]);

  const handleCloseModal = () => {
    setShowModal(false);
    resetMission();
  };

  const isSuccess = missionState.correctAnswer === missionState.userAnswer;

  if (missionState.isStarted && importer) {
    return (
      <>
        <GlowEffect blur='stronger' scale={0.9} className="rounded-2xl" />
        <div className="bg-neutral-100 border-none gradient-border-1 gradient-border-conic-vibrant rounded-2xl h-full min-h-[400px] overflow-hidden">
          <MissionLoader
            importer={importer}
            minDuration={1000} // délai minimum d'affichage du loader en ms
            fallback={
              <div className="flex items-center justify-center h-full text-lg">
                <TextShimmer as='span' spread={2} duration={5}>
                  Chargement de la mission...
                </TextShimmer>
              </div>
            }
          />
        </div>
        <MissionModal
          hidden={hidden}
          setHidden={setHidden}
          isOpen={showModal}
          isSuccess={isSuccess}
          mission={mission}
          parcours={parcours}
          currentMissionIndex={currentMissionIndex}
          onClose={handleCloseModal}
        />
      </>
    );
  }

  return (
    <div className="bg-neutral-100 border border-neutral-200 rounded-2xl h-full min-h-[400px] flex items-center justify-center">
      <div className="text-center p-8">
        <Paragraph className="!text-lg">
          <TextShimmer as='span' spread={2} duration={5}>
            En attente du lancement de la mission
          </TextShimmer>
          <br/>
          <span className="text-sm !text-primary-400">
            (clique sur le bouton "Accepter et démarrer")
          </span>
        </Paragraph>
      </div>
    </div>
  );
};